import { useToast } from '@chakra-ui/react';
import React, { useCallback } from 'react';
import {
  RouteProps as ReactDOMRouteProps,
  Route as ReactDOMRoute,
  Redirect,
} from 'react-router-dom';
import { STORAGE, ROUTER_PATHS } from '../constants';
import { useAuth } from '../hooks/useAuth';
import { useTelemetry } from '../hooks';
import api from '../utils/api';
import apiSinister from '~/utils/apiSinister';
import apiSSO from '~/utils/sso';
import axiosPrivate from '~/utils/axiosPrivate';
import apiAssist from '~/utils/apiAssist';
import AuthGuard from '~/guards/AuthGuard';
import LoginGuard from '~/guards/LoginGuard';
import Login2faGuard from '~/guards/Login2faGuard';

interface RouteProps extends ReactDOMRouteProps {
  isPrivate?: boolean;
  path: string;
  component: React.ComponentType;
}

export const Route: React.FC<RouteProps> = ({
  isPrivate = false,
  component: Component,
  location,
  ...props
}) => {
  const { isStorageValid, clearLocalStorage, user } = useAuth();
  const { urlChanged } = useTelemetry();
  const toast = useToast();

  api.interceptors.response.use(
    response => {
      return response;
    },
    // eslint-disable-next-line consistent-return
    err => {
      // if (err.message === 'Network Error') signOut();

      const refreshToken = localStorage.getItem(`${STORAGE}:refreshToken`);
      if (err.response?.status === 401 && !!refreshToken) {
        axiosPrivate
          .post('/auth/signin/refresh', {
            refresh: refreshToken,
          })
          .then(response => {
            localStorage.setItem(`${STORAGE}:token`, response.data.access);
            localStorage.setItem(
              `${STORAGE}:refreshToken`,
              response.data.refresh,
            );
          })
          .catch(error => console.error(error));
        return api(err?.config);
      }
      return Promise.reject(err);
    },
  );

  apiSinister.interceptors.response.use(
    response => {
      return response;
    },
    // eslint-disable-next-line consistent-return
    err => {
      // if (err.message === 'Network Error') signOut();
      const refreshToken = localStorage.getItem(`${STORAGE}:refreshToken`);
      if (err.response?.status === 401 && !!refreshToken) {
        axiosPrivate
          .post('/auth/signin/refresh', {
            refresh: refreshToken,
          })
          .then(response => {
            localStorage.setItem(`${STORAGE}:token`, response.data.access);
            localStorage.setItem(
              `${STORAGE}:refreshToken`,
              response.data.refresh,
            );
          })
          .catch(error => console.error(error));
        return apiSinister(err?.config);
      }
      return Promise.reject(err);
    },
  );

  apiSSO.interceptors.response.use(
    response => {
      return response;
    },
    // eslint-disable-next-line consistent-return
    err => {
      // if (err.message === 'Network Error') signOut();
      const refreshToken = localStorage.getItem(`${STORAGE}:refreshToken`);
      if (err.response?.status === 401 && !!refreshToken) {
        axiosPrivate
          .post('/auth/signin/refresh', {
            refresh: refreshToken,
          })
          .then(response => {
            localStorage.setItem(`${STORAGE}:token`, response.data.access);
            localStorage.setItem(
              `${STORAGE}:refreshToken`,
              response.data.refresh,
            );
          })
          .catch(error => console.error(error));
        return apiSSO(err?.config);
      }
      return Promise.reject(err);
    },
  );

  apiAssist.interceptors.response.use(
    response => {
      return response;
    },
    // eslint-disable-next-line consistent-return
    err => {
      // if (err.message === 'Network Error') signOut();
      const refreshToken = localStorage.getItem(`${STORAGE}:refreshToken`);
      if (err.response?.status === 401 && !!refreshToken) {
        axiosPrivate
          .post('/auth/signin/refresh', {
            refresh: refreshToken,
          })
          .then(response => {
            localStorage.setItem(`${STORAGE}:token`, response.data.access);
            localStorage.setItem(
              `${STORAGE}:refreshToken`,
              response.data.refresh,
            );
          })
          .catch(error => console.error(error));
        return apiAssist(err?.config);
      }
      return Promise.reject(err);
    },
  );

  axiosPrivate.interceptors.response.use(
    response => {
      return response;
    },
    err => {
      if (err.response?.status === 401) {
        const refreshToken = localStorage.getItem(`${STORAGE}:refreshToken`);
        clearLocalStorage();
        if (refreshToken) {
          toast({
            title: 'Sessão expirada',
            description: 'Entre novamente',
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        }
      }
      return Promise.reject(err);
    },
  );

  const handleRender = useCallback(() => {
    location && urlChanged(location.pathname);

    return location && location.pathname.includes('/portal') ? (
      <AuthGuard component={<Component />} />
    ) : location && location.pathname === '/login' ? (
      <LoginGuard component={<Component />} />
    ) : location && location.pathname === '/verify-code' ? (
      <Login2faGuard component={<Component />} />
    ) : (
      <Component />
    );
  }, [isStorageValid, isPrivate, Component, user]);

  return <ReactDOMRoute {...props} render={handleRender} />;
};
